<template>
  <AppHeaderLayout>
    <div class="page">
      <header class="page-header">
        <h1>客戶管理</h1>
        <button class="btn primary" type="button" @click="openCreate">新增客戶</button>
      </header>

      <section class="search-card">
        <div class="search-grid">
          <label class="form-field">
            <span>搜尋</span>
            <input v-model="search.name" type="text" placeholder="搜尋客戶名稱" />
          </label>
        </div>
      </section>

      <div class="table-card">
        <table>
          <thead>
            <tr>
              <th>客戶名稱</th>
              <th>電話</th>
              <th>地址</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="company in filteredCompanies" :key="company.companyID">
              <td>{{ company.name }}</td>
              <td>{{ company.tel }}</td>
              <td>{{ company.address }}</td>
              <td>
                <button class="link-button" type="button" @click="startEdit(company)">編輯</button>
              </td>
            </tr>
            <tr v-if="isLoading">
              <td colspan="4" class="empty">資料載入中...</td>
            </tr>
            <tr v-else-if="errorMessage">
              <td colspan="4" class="empty">{{ errorMessage }}</td>
            </tr>
            <tr v-else-if="filteredCompanies.length === 0">
              <td colspan="4" class="empty">無資料</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <div class="page-size">
          <span>每頁顯示</span>
          <select v-model.number="pageSize">
            <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
          </select>
          <span>筆</span>
          <span>共 {{ totalCount }} 筆</span>
        </div>

        <div class="pager">
          <button type="button" :disabled="!canPrev || isLoading" @click="prevPage" aria-label="上一頁">
            &lt;
          </button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button type="button" :disabled="!canNext || isLoading" @click="nextPage" aria-label="下一頁">
            &gt;
          </button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ modalTitle }}</h2>
          <button class="modal-close" type="button" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <label class="form-field">
              <span>客戶名稱</span>
              <input v-model="form.name" type="text" placeholder="請輸入客戶名稱" />
            </label>
            <label class="form-field">
              <span>電話</span>
              <input v-model="form.tel" type="text" placeholder="請輸入電話" />
            </label>
            <label class="form-field span-all">
              <span>地址</span>
              <input v-model="form.address" type="text" placeholder="請輸入地址" />
            </label>
          </div>
          <div v-if="formError" class="form-error">{{ formError }}</div>
        </div>
        <div class="modal-footer">
          <div class="modal-actions">
            <button class="btn primary" type="button" :disabled="isSubmitting" @click="submitForm">
              {{ isSubmitting ? "儲存中..." : submitLabel }}
            </button>
            <button class="btn ghost" type="button" :disabled="isSubmitting" @click="closeModal">取消</button>
          </div>
        </div>
      </div>
    </div>
  </AppHeaderLayout>
</template>

<script>
import AppHeaderLayout from "@/components/AppHeaderLayout.vue";
import {
  ADD_COMPANY,
  GET_ALL_COMPANY,
  MODIFY_COMPANY_BY_ID
} from "@/utils/requestUrl";
import { getToken } from "@/utils/sessionStoraggeUtil";

export default {
  name: "CompanyManageView",
  components: {
    AppHeaderLayout
  },
  data() {
    return {
      companies: [],
      form: {
        name: "",
        tel: "",
        address: ""
      },
      search: {
        name: ""
      },
      showModal: false,
      editingCompanyId: null,
      pageSize: 10,
      pageSizes: [10, 50, 100],
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      isLoading: false,
      isSubmitting: false,
      errorMessage: "",
      formError: ""
    };
  },
  computed: {
    submitLabel() {
      return this.editingCompanyId === null ? "新增" : "儲存";
    },
    modalTitle() {
      return this.editingCompanyId === null ? "新增客戶" : "編輯客戶";
    },
    filteredCompanies() {
      const keyword = this.search.name.trim().toLowerCase();
      return this.companies.filter((company) => {
        if (!keyword) {
          return true;
        }
        return (company.name || "").toLowerCase().includes(keyword);
      });
    },
    canPrev() {
      return this.currentPage > 1;
    },
    canNext() {
      return this.currentPage < this.totalPages;
    }
  },
  watch: {
    pageSize() {
      this.currentPage = 1;
      this.fetchCompanies();
    }
  },
  created() {
    this.fetchCompanies();
  },
  methods: {
    getApiBase() {
      if (window._env_ && window._env_.VUE_APP_REQUEST_DOMAIN) {
        return window._env_.VUE_APP_REQUEST_DOMAIN;
      }
      return "";
    },
    buildHeaders() {
      const headers = {
        "Content-Type": "application/json"
      };
      const token = getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return headers;
    },
    getUserMemberId() {
      const userInfo = this.$store.getters.getUserInfo || {};
      return userInfo.memberId || userInfo.MemberId || "";
    },
    mapCompany(item) {
      return {
        companyID: Number(item.companyID ?? item.CompanyID ?? 0),
        name: item.name || item.Name || "",
        tel: item.tel || item.Tel || "",
        address: item.address || item.Address || ""
      };
    },
    async fetchCompanies() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const response = await fetch(this.getApiBase() + GET_ALL_COMPANY, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            PageIndex: this.currentPage,
            PageSize: this.pageSize
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok) {
          throw new Error((payload && payload.message) || "取得客戶資料失敗");
        }

        if (payload && payload.status === "failure") {
          throw new Error(payload.message || "取得客戶資料失敗");
        }

        const data = (payload && payload.data) || {};
        const list = data.companyList || data.CompanyList || [];
        const totalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
        const totalPages = Number(data.totalPages ?? data.TotalPages ?? 0);

        this.companies = list.map(this.mapCompany);
        this.totalCount = totalCount;
        this.totalPages = totalPages > 0 ? totalPages : 1;

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
          await this.fetchCompanies();
        }
      } catch (error) {
        this.companies = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.errorMessage = error.message || "取得客戶資料失敗";
      } finally {
        this.isLoading = false;
      }
    },
    openCreate() {
      this.resetForm();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    async submitForm() {
      this.formError = "";

      if (!this.form.name || !this.form.tel || !this.form.address) {
        this.formError = "請完整輸入客戶名稱、電話與地址";
        return;
      }

      const memberId = this.getUserMemberId();
      if (!memberId) {
        this.formError = "無法取得登入者資訊，請重新登入後再試";
        return;
      }

      this.isSubmitting = true;
      try {
        const isCreate = this.editingCompanyId === null;
        const url = isCreate ? ADD_COMPANY : MODIFY_COMPANY_BY_ID;
        const body = isCreate
          ? {
              Name: this.form.name,
              Tel: this.form.tel,
              Address: this.form.address,
              Creator: memberId
            }
          : {
              CompanyID: this.editingCompanyId,
              Name: this.form.name,
              Tel: this.form.tel,
              Address: this.form.address,
              Updator: memberId
            };

        const response = await fetch(this.getApiBase() + url, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify(body)
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok) {
          throw new Error((payload && payload.message) || "儲存客戶資料失敗");
        }

        if (payload && payload.status === "failure") {
          throw new Error(payload.message || "儲存客戶資料失敗");
        }

        this.closeModal();
        if (isCreate) {
          this.currentPage = 1;
        }
        await this.fetchCompanies();
      } catch (error) {
        this.formError = error.message || "儲存客戶資料失敗";
      } finally {
        this.isSubmitting = false;
      }
    },
    startEdit(company) {
      this.editingCompanyId = company.companyID;
      this.form.name = company.name;
      this.form.tel = company.tel;
      this.form.address = company.address;
      this.formError = "";
      this.showModal = true;
    },
    resetForm() {
      this.editingCompanyId = null;
      this.form.name = "";
      this.form.tel = "";
      this.form.address = "";
      this.formError = "";
    },
    async prevPage() {
      if (this.canPrev) {
        this.currentPage -= 1;
        await this.fetchCompanies();
      }
    },
    async nextPage() {
      if (this.canNext) {
        this.currentPage += 1;
        await this.fetchCompanies();
      }
    }
  }
};
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 12px 24px rgba(15, 30, 40, 0.08);
  font-size: 1rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 1.625rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9375rem;
}

.form-field input {
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 0.9375rem;
}

.form-field input:focus {
  outline: none;
  border-color: rgba(10, 160, 165, 0.7);
  box-shadow: 0 0 0 3px rgba(10, 160, 165, 0.15);
}

.form-field.span-all {
  grid-column: 1 / -1;
}

.form-error {
  color: #b91c1c;
  font-size: 0.875rem;
}

.btn {
  min-width: 90px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 0.9375rem;
}

.btn.primary {
  background: #0aa0a5;
  color: #ffffff;
}

.btn.ghost {
  background: #eef2f4;
  color: #1f2a33;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.search-card {
  border-radius: 14px;
  border: 1px solid rgba(15, 30, 40, 0.08);
  padding: 16px;
  margin-bottom: 20px;
  background: #ffffff;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
}

.table-card thead {
  background: #f4f7f8;
  text-align: left;
}

.table-card th,
.table-card td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card tbody tr:hover {
  background: rgba(10, 160, 165, 0.06);
}

.link-button {
  border: none;
  background: transparent;
  color: #0aa0a5;
  cursor: pointer;
  padding: 0;
  font-size: 0.9375rem;
}

.link-button:hover {
  text-decoration: underline;
}

.empty {
  text-align: center;
  padding: 24px 0;
  color: rgba(31, 42, 51, 0.5);
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  gap: 16px;
  flex-wrap: wrap;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
}

.page-size select {
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  padding: 4px 8px;
  font-size: 0.9375rem;
}

.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9375rem;
}

.pager button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(15, 30, 40, 0.2);
  background: #ffffff;
  cursor: pointer;
}

.pager button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 16, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.modal {
  width: min(600px, 92vw);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(15, 30, 40, 0.2);
  padding: 20px 22px 18px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.375rem;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 1.625rem;
  cursor: pointer;
  line-height: 1;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.modal-actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 960px) {
  .search-grid,
  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .page {
    padding: 20px;
  }

  .search-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .table-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
